import React, { useEffect, useState } from 'react';
import { Header, Icon, Loader, Message, Segment, Select, Statistic } from 'semantic-ui-react';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { getServerMetrics } from '../../services/Metrics';
import { MONTH_OPTIONS, getCurrentPeriod } from './metricsHelpers';

function MetricsServerSection() {
  const [period, setPeriod] = useState(getCurrentPeriod());
  const [state, setState] = useState({ data: null, loading: true, error: false });

  useEffect(() => {
    setState((s) => ({ ...s, loading: true, error: false }));
    getServerMetrics(period)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          setState({ data: res.data.result, loading: false, error: false });
        } else {
          setState({ data: null, loading: false, error: true });
        }
      })
      .catch(() => setState({ data: null, loading: false, error: true }));
  }, [period]);

  return (
    <Segment className="transparent-table">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1em',
        }}
      >
        <Header as="h3" color="teal" style={{ margin: 0 }}>
          <Icon name="chart bar" />
          <Header.Content>Activité serveur</Header.Content>
        </Header>
        <Select
          options={MONTH_OPTIONS}
          value={period}
          onChange={(_, { value }) => setPeriod(value)}
          compact
        />
      </div>
      {state.loading && <Loader active inline="centered" />}
      {state.error && <Message error content="Impossible de charger les métriques serveur." />}
      {!state.loading &&
        state.data &&
        (() => {
          const d = state.data;
          const totalSessions = d.session_start?.count ?? 0;
          const totalDownloads = (d.download_browser?.count ?? 0) + (d.download_device?.count ?? 0);
          const totalTorrents = Object.entries(d)
            .filter(([k]) => k.startsWith('torrent_add_'))
            .reduce((acc, [, v]) => acc + v.count, 0);
          const totalSearches =
            (d.search_topbar?.count ?? 0) +
            (d.search_no_result?.count ?? 0) +
            (d.search_calendar?.count ?? 0);
          const chartData = Object.entries(d)
            .map(([name, v]) => ({ name, count: v.count }))
            .sort((a, b) => b.count - a.count);
          return (
            <>
              <Statistic.Group widths={4} size="mini" style={{ marginBottom: '1.5em' }}>
                <Statistic>
                  <Statistic.Value>{totalSessions}</Statistic.Value>
                  <Statistic.Label>Sessions</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{totalSearches}</Statistic.Value>
                  <Statistic.Label>Recherches</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{totalDownloads}</Statistic.Value>
                  <Statistic.Label>Téléchargements</Statistic.Label>
                </Statistic>
                <Statistic>
                  <Statistic.Value>{totalTorrents}</Statistic.Value>
                  <Statistic.Label>Torrents ajoutés</Statistic.Label>
                </Statistic>
              </Statistic.Group>
              {chartData.length > 0 && (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10 }}
                      interval={0}
                      angle={-30}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#00b5ad" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </>
          );
        })()}
    </Segment>
  );
}

export default MetricsServerSection;
